import DetailsCard from "@/components/DetailsCard";
import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { useNavigate, useParams } from "react-router-dom";
import ActiveYearsBreakdownModal from "@/pages/people/[pgaId]/membership/_components/ActiveYearsBreakdownModal";
import useActiveYearsBreakdown from "@/pages/people/[pgaId]/membership/_components/useActiveYearsBreakdown";
import { Person, ProgramTypeName } from "@/types";
import { isValidSuggestedNote, MemberInsights } from "./MemberInsights";
import {
  INSIGHTS_QUERY,
  PERSON_OVERVIEW_CONTACT_QUERY,
  PERSON_OVERVIEW_EDUCATION_QUERY,
  PERSON_OVERVIEW_MEMBERSHIP_QUERY,
  PERSON_OVERVIEW_CAREER_QUERY,
  PERSON_OVERVIEW_GROUPS_QUERY
} from "../queries";
import { OverviewPersonHistory } from "./OverviewPersonHistory";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { ccmsSuspensionsForStatus } from "@/features/person/utils/ccmsSuspensions";
import { usePersonProfileReadCapabilities } from "@/features/person/hooks/usePersonProfileReadCapabilities";
import {
  emailOptions,
  phoneOptions,
  sectionOptions,
  committeesOptions,
  qualifyingLevelData,
  backgroundCheckData,
  buildPATStatusForNonMembers,
  hasMeaningfulPATStatus,
  careerOptions,
  buildProgramStatusCard,
  educationLearningOptions,
  membershipOptions,
  isMemberOrAssociateProgram,
  overlayActiveProgramStatusWithOpenCcmsSuspension
} from "@/features/person/utils/utils";
import { buildAssociateCurrentPgmMetric } from "@/features/person/utils/associateCurrentPgmMetric";
import { buildNoProgramOverviewMetrics } from "@/features/person/utils/noProgramOverviewMetrics";
import { buildProspectProgramStatusMetric } from "@/features/person/utils/prospectProgramStatusMetric";
import MetricGrid from "@/components/MetricGrid";

import { toUTCFormat } from "@/lib/dateHelpers";
import { hasDuesPayingProgram } from "@/pages/people/_utils";
import { ProgramStatus } from "@/types";
import { useDuesStatus } from "@/hooks/useDuesStatus";
import { usePdrStatus } from "@/hooks/usePdrStatus";
import { useQuery } from "@apollo/client";

import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import {
  PAT_ACTIVITY_SCROLL_ID,
  membershipProgressCardNavigation,
  type MembershipProgressLinkOptions
} from "@/features/person/utils/membershipProgressLinks";

interface Props {
  person: Person;
}

type BuildMetricsArgs = {
  person: Person;
  pdrMetric?: MetricCardType<Path>;
  duesMetric?: MetricCardType<Path>;
  navigate?: (to: string) => void;
  onOpenActiveYearsBreakdown?: () => void;
  activeYearsValue?: string | null;
  canManageJobPosts?: boolean;
  canViewPersonGroups?: boolean;
  canViewSuspensionHistory?: boolean;
};

function buildProgramStatusMetric(
  person: Person,
  navigate?: (to: string) => void,
  canViewSuspensionHistory = false
): MetricCardType<Path> | null {
  const { latestProgram } = person;
  const displayedStatus = overlayActiveProgramStatusWithOpenCcmsSuspension(
    latestProgram?.status,
    person.ccmsSuspensions
  );
  if (displayedStatus !== ProgramStatus.SUSPENDED && displayedStatus !== ProgramStatus.TERMINATED) {
    return null;
  }

  const card = buildProgramStatusCard(
    latestProgram?.status,
    person.ccmsSuspensions,
    latestProgram?.band?.terminatedReason,
    latestProgram?.band?.terminatesOn,
    "Program Status"
  );

  return {
    ...card,
    iconName: "badge",
    ...(displayedStatus === ProgramStatus.SUSPENDED &&
      person.pgaId &&
      canViewSuspensionHistory && {
        onClick: () => navigate?.(`/people/${person.pgaId}/membership/overview?scrollTo=suspension-history`)
      })
  };
}

function patOverviewMetric(person: Person, { navigate }: Pick<BuildMetricsArgs, "navigate">): MetricCardType<Path> {
  const playingAbilityTestStatus = person.prospectProgram?.playingAbilityTestStatus;
  const hasPatData = hasMeaningfulPATStatus(playingAbilityTestStatus);
  const patStatus =
    hasPatData && playingAbilityTestStatus
      ? buildPATStatusForNonMembers(playingAbilityTestStatus)
      : { value: "Not Started" as const, variant: undefined, tooltip: undefined };

  return {
    label: "Playing Ability Test (PAT)",
    value: patStatus.value,
    iconName: "sports_golf",
    ...(patStatus.variant && { variant: patStatus.variant }),
    ...(patStatus.tooltip && { tooltip: patStatus.tooltip }),
    ...membershipProgressCardNavigation(person.pgaId, {
      scrollTo: PAT_ACTIVITY_SCROLL_ID,
      navigate
    })
  };
}

function membershipProgressOptions({ navigate }: Pick<BuildMetricsArgs, "navigate">): MembershipProgressLinkOptions {
  return { navigate };
}

/** Fallback metrics for program types outside the program-type matrix. */
function buildLegacyOverviewMetrics({
  person,
  pdrMetric,
  duesMetric,
  navigate,
  onOpenActiveYearsBreakdown,
  activeYearsValue,
  canViewSuspensionHistory
}: BuildMetricsArgs): MetricCardType<Path>[] {
  const { membershipSince, latestProgram } = person;
  const programTypeName = latestProgram?.programType?.name;
  const isTerminated = latestProgram?.status === ProgramStatus.TERMINATED;

  const metrics: MetricCardType<Path>[] = [];

  const programStatus = buildProgramStatusMetric(person, navigate, canViewSuspensionHistory);
  if (programStatus) metrics.push(programStatus);

  if (programTypeName && !isTerminated) {
    metrics.push({
      label: `${programTypeName} Since`,
      value: toUTCFormat(membershipSince || ""),
      iconName: "badge",
      ...(person.pgaId && {
        link: {
          to: "/people/:pgaId/membership/overview",
          params: { pgaId: person.pgaId }
        }
      })
    });
  }

  if (programTypeName === "Member" && activeYearsValue != null) {
    const programSinceFormatted = toUTCFormat(membershipSince || "");
    metrics.push({
      label: programSinceFormatted
        ? `Active Years of Membership\nElection Date: ${programSinceFormatted}`
        : "Active Years of Membership",
      value: activeYearsValue,
      iconName: "schedule",
      ...(onOpenActiveYearsBreakdown && {
        onClick: onOpenActiveYearsBreakdown,
        tooltip: {
          ...(programSinceFormatted && { title: `Member since ${programSinceFormatted}` }),
          supportingText: "View how each membership band contributes to this total."
        }
      })
    });
  }

  const qualifyingLevel = qualifyingLevelData(person);
  if (qualifyingLevel) {
    metrics.push({
      label: "Qualifying Level Courses",
      value: qualifyingLevel.status,
      iconName: "task_alt"
    });
  }

  const backgroundCheck = backgroundCheckData(person);
  if (backgroundCheck) {
    metrics.push({
      label: "Background Check",
      value: backgroundCheck.status,
      iconName: "task_alt"
    });
  }

  const programName = person.latestProgram?.programType.name;

  if (programName && programName !== ProgramTypeName.Member) {
    metrics.push(patOverviewMetric(person, { navigate }));
  }

  if (pdrMetric) {
    metrics.push(pdrMetric);
  }

  if (hasDuesPayingProgram(person) && duesMetric) {
    metrics.push(duesMetric);
  }

  return metrics;
}

/**
 * Program-type metric matrix.
 * PR2B: Member opted in (Active Years → PDR → Dues).
 * PR2C: Associate / Affiliate / Student PGM track.
 * PR2D: Prospect (Since → Prospect Program Status → PAT).
 * PR2E: No program (Account Created + conditional Entry Point / Groups / Job Applications).
 */
function buildProgramTypeOverviewMetrics({
  person,
  pdrMetric,
  duesMetric,
  navigate,
  onOpenActiveYearsBreakdown,
  activeYearsValue,
  canManageJobPosts,
  canViewPersonGroups,
  canViewSuspensionHistory
}: BuildMetricsArgs): MetricCardType<Path>[] {
  const { membershipSince, latestProgram } = person;
  const programTypeName = latestProgram?.programType?.name;
  const isTerminated = latestProgram?.status === ProgramStatus.TERMINATED;
  const progressLinkOptions = membershipProgressOptions({ navigate });

  const isPgmTrack =
    programTypeName === ProgramTypeName.Associate ||
    programTypeName === ProgramTypeName.Affiliate ||
    programTypeName === ProgramTypeName.Student;
  const isProspect = programTypeName === ProgramTypeName.Prospect;

  if (!latestProgram) {
    return buildNoProgramOverviewMetrics(person, { canManageJobPosts, canViewPersonGroups, ...progressLinkOptions });
  }

  if (programTypeName !== ProgramTypeName.Member && !isPgmTrack && !isProspect) {
    return buildLegacyOverviewMetrics({
      person,
      pdrMetric,
      duesMetric,
      navigate,
      onOpenActiveYearsBreakdown,
      activeYearsValue,
      canViewSuspensionHistory
    });
  }

  const programSinceFormatted = toUTCFormat(membershipSince || "");
  const metrics: MetricCardType<Path>[] = [];

  const programStatus = buildProgramStatusMetric(person, navigate, canViewSuspensionHistory);
  if (programStatus) metrics.push(programStatus);

  if (programTypeName === ProgramTypeName.Member && activeYearsValue != null) {
    metrics.push({
      label: programSinceFormatted
        ? `Active Years of Membership\nElection Date: ${programSinceFormatted}`
        : "Active Years of Membership",
      value: activeYearsValue,
      iconName: "schedule",
      ...(onOpenActiveYearsBreakdown && {
        onClick: onOpenActiveYearsBreakdown,
        tooltip: {
          ...(programSinceFormatted && { title: `Member since ${programSinceFormatted}` }),
          supportingText: "View how each membership band contributes to this total."
        }
      })
    });
  } else if (!isTerminated) {
    metrics.push({
      label: `${programTypeName} Since`,
      value: programSinceFormatted,
      iconName: "badge",
      ...(person.pgaId && {
        link: {
          to: "/people/:pgaId/membership/overview",
          params: { pgaId: person.pgaId }
        }
      })
    });
  }

  if (programTypeName === ProgramTypeName.Student && !isTerminated && person.universityCohort !== undefined) {
    const cohortName = person.universityCohort?.name;
    metrics.push({
      label: "Cohort",
      value: cohortName || programSinceFormatted || "--",
      iconName: "today",
      ...(cohortName &&
        programSinceFormatted && {
          tooltip: { supportingText: `Enrolled ${programSinceFormatted}` }
        })
    });
  }

  if (isPgmTrack) {
    const isPgmSuspended = !!person.pgmProgressStatus?.isGptpSuspended;
    const pgmMetric = buildAssociateCurrentPgmMetric(person, isPgmSuspended, progressLinkOptions);
    if (pgmMetric) {
      metrics.push(pgmMetric);
    }
  }

  if (isProspect) {
    metrics.push(buildProspectProgramStatusMetric(person, progressLinkOptions));
  }

  if (programTypeName !== ProgramTypeName.Member) {
    metrics.push(patOverviewMetric(person, { navigate }));
  }

  if ((programTypeName === ProgramTypeName.Member || programTypeName === ProgramTypeName.Associate) && pdrMetric) {
    metrics.push(pdrMetric);
  }

  if (hasDuesPayingProgram(person) && duesMetric) {
    metrics.push(duesMetric);
  }

  return metrics;
}

export function OverviewDetails({ person }: Props) {
  const navigate = useNavigate();
  const { pgaId } = useParams();
  const {
    canViewCareerPreferences,
    canViewCompensationProfiles,
    canManageJobPosts,
    canViewPersonContact,
    canViewPersonMembership,
    canViewPersonEducation,
    canViewPersonCareer,
    canViewPersonBilling,
    canViewPersonGroups,
    canViewPersonInsights,
    canViewOrders,
    canViewPersonSuspensionHistory
  } = usePersonProfileReadCapabilities();
  const canViewSection = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewSection
  });

  const { data: overviewContactData } = useQuery(PERSON_OVERVIEW_CONTACT_QUERY, {
    variables: { pgaId: person.pgaId },
    skip: !person.pgaId || !canViewPersonContact,
    fetchPolicy: "network-only"
  });

  const { data: overviewMembershipData, loading: overviewMembershipLoading } = useQuery(
    PERSON_OVERVIEW_MEMBERSHIP_QUERY,
    {
      variables: {
        pgaId: person.pgaId,
        includeProgramMetrics: true
      },
      skip: !person.pgaId || !canViewPersonMembership,
      fetchPolicy: "network-only"
    }
  );

  const { data: overviewEducationData, loading: overviewEducationLoading } = useQuery(PERSON_OVERVIEW_EDUCATION_QUERY, {
    variables: {
      pgaId: person.pgaId,
      includeProgramMetrics: true,
      includeUniversityCohort: canViewSection
    },
    skip: !person.pgaId || !canViewPersonEducation,
    fetchPolicy: "network-only"
  });

  const { data: overviewCareerData, loading: overviewCareerLoading } = useQuery(PERSON_OVERVIEW_CAREER_QUERY, {
    variables: { pgaId: person.pgaId },
    skip: !person.pgaId || !canViewPersonCareer,
    fetchPolicy: "network-only"
  });

  const { data: overviewGroupsData, loading: overviewGroupsLoading } = useQuery(PERSON_OVERVIEW_GROUPS_QUERY, {
    variables: { pgaId: person.pgaId ?? "" },
    skip: !person.pgaId || !canViewPersonGroups,
    fetchPolicy: "network-only"
  });

  const overviewContactPerson = overviewContactData?.person;
  const overviewMembershipPerson = overviewMembershipData?.person;
  const overviewEducationPerson = overviewEducationData?.person;
  const overviewCareerPerson = overviewCareerData?.person;
  const overviewGroupsPerson = overviewGroupsData?.person;
  const personForMembershipDisplay = overviewMembershipPerson
    ? {
        ...person,
        ...overviewMembershipPerson,
        ccmsSuspensions: ccmsSuspensionsForStatus(overviewMembershipPerson)
      }
    : person;
  const personForEducationDisplay = overviewEducationPerson
    ? { ...personForMembershipDisplay, ...overviewEducationPerson }
    : personForMembershipDisplay;
  const personForCareerDisplay = overviewCareerPerson
    ? { ...personForEducationDisplay, ...overviewCareerPerson }
    : personForEducationDisplay;
  const personForGroupsDisplay =
    canViewPersonGroups && overviewGroupsPerson?.groupAssignments !== undefined
      ? { ...personForCareerDisplay, groupAssignments: overviewGroupsPerson.groupAssignments }
      : { ...personForCareerDisplay, groupAssignments: undefined };

  const { data: insightsData } = useQuery(INSIGHTS_QUERY, {
    variables: { pgaId: person.pgaId ?? "" },
    skip:
      !person.pgaId ||
      !canViewPersonInsights ||
      !canViewPersonMembership ||
      !overviewMembershipPerson?.latestProgram?.programType,
    fetchPolicy: "cache-and-network"
  });

  const { pdrMetric } = usePdrStatus(person.pgaId, { skip: !canViewPersonEducation });
  const { duesMetric } = useDuesStatus(personForMembershipDisplay, {
    skip: !canViewPersonBilling,
    skipOrders: !canViewOrders
  });
  const { breakdownOpen, openBreakdown, closeBreakdown, activeYearsValue } = useActiveYearsBreakdown(person);

  const membershipMetricsReady = !canViewPersonMembership || !overviewMembershipLoading;
  const educationMetricsReady = !canViewPersonEducation || !overviewEducationLoading;
  const careerMetricsReady = !canViewPersonCareer || !overviewCareerLoading;
  const groupsMetricsReady = !canViewPersonGroups || !overviewGroupsLoading;
  const metricsReady = membershipMetricsReady && educationMetricsReady && careerMetricsReady && groupsMetricsReady;
  const isMemberOrAssociate =
    membershipMetricsReady && canViewPersonMembership && isMemberOrAssociateProgram(personForMembershipDisplay);

  const metricsArgs: BuildMetricsArgs = {
    person: personForGroupsDisplay,
    pdrMetric: canViewPersonEducation ? pdrMetric : undefined,
    duesMetric: canViewPersonBilling ? duesMetric : undefined,
    navigate,
    onOpenActiveYearsBreakdown: person.pgaId ? openBreakdown : undefined,
    activeYearsValue,
    canManageJobPosts,
    canViewPersonGroups,
    canViewSuspensionHistory: canViewPersonSuspensionHistory
  };

  const metrics = metricsReady && canViewPersonMembership ? buildProgramTypeOverviewMetrics(metricsArgs) : [];
  const suggestedNote = insightsData?.insights?.suggestedNote;
  const hasInsights = canViewPersonInsights && isValidSuggestedNote(suggestedNote);
  return (
    <div className="flex flex-col min-w-0" data-testid="overview-details">
      {hasInsights && <MemberInsights suggestedNote={suggestedNote} />}
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      {person.pgaId && <OverviewPersonHistory pgaId={person.pgaId} />}
      <DetailsCardThreeColumnRow tabBodySpacing>
        {canViewPersonCareer && careerMetricsReady && (
          <DetailsCard
            heading="Career"
            options={careerOptions(
              personForCareerDisplay,
              pgaId,
              navigate,
              canViewCareerPreferences,
              canViewCompensationProfiles
            )}
            skipRenderWithEmptyOptions
          />
        )}
        {metricsReady &&
          (isMemberOrAssociate ? (
            <MultiSectionDetailsCard
              sections={[
                {
                  heading: "Committees",
                  options: committeesOptions(personForMembershipDisplay, navigate)
                },
                ...(canViewPersonEducation
                  ? [
                      {
                        heading: "PGA Lifelong Learning",
                        options: educationLearningOptions(personForEducationDisplay, navigate)
                      }
                    ]
                  : [])
              ]}
            />
          ) : overviewContactPerson ? (
            <MultiSectionDetailsCard
              sections={[
                {
                  heading: "Phone",
                  options: phoneOptions(overviewContactPerson.phoneNumbers)
                },
                {
                  heading: "Email",
                  options: emailOptions(overviewContactPerson.email)
                }
              ]}
            />
          ) : null)}
        {person.primarySection && (
          <MultiSectionDetailsCard
            sections={[
              {
                heading: "Section",
                options: sectionOptions(personForMembershipDisplay, true, navigate)
              },
              ...(canViewPersonMembership && overviewMembershipPerson?.latestProgram
                ? [
                    {
                      heading: "Membership",
                      options: membershipOptions(personForMembershipDisplay, navigate)
                    }
                  ]
                : [])
            ]}
          />
        )}
      </DetailsCardThreeColumnRow>
      {breakdownOpen && person.pgaId && (
        <ActiveYearsBreakdownModal
          open={breakdownOpen}
          onClose={closeBreakdown}
          pgaId={person.pgaId}
          activeYearsValue={activeYearsValue}
        />
      )}
    </div>
  );
}
